import { defineStore } from 'pinia';
import { makeCallToVersionAPI } from '@/worker/worker';
import { RepositoryFactory } from '@/repositories';
import { clearRequestCache } from '@/repositories/repository';
import {
	broadcastLogoutAndSync,
	broadcastLoginAndSync,
} from '@/worker/shareSession';
import { popupService } from '@arritech/ui/Popup/Popup.service';
import { toastService } from '@arritech/ui/Toast';
import { parseJwt } from '@arritech/shared/utils';
import { Utils } from '@/common';
import {
	parseModuleAvailability,
	type ModuleAvailability,
} from '@/common/moduleAvailability';
import {
	ModuleRole,
	SystemClaim,
	satisfiesClaim,
	satisfiesModuleRole,
	toClaimArray,
} from '@/common/rbac';
import {
	AUTH_ERROR,
	AUTH_IMPERSONATION_REQUEST,
	AUTH_LOGOUT,
	AUTH_REQUEST,
	AUTH_RESET_IMPERSONATION_REQUEST,
	AUTH_SUCCESS,
} from '@/store/actions';

const getPublicRepository = () => RepositoryFactory.get('public');
const getAuthRepository = () => RepositoryFactory.get('auth');
const getMerchantRepository = () => RepositoryFactory.get('merchant');
const getBillingRepository = () => RepositoryFactory.get('billing');
const getUserPreferenceRepository = () =>
	RepositoryFactory.get('userPreferences');

type MerchantActor = {
	id: string;
	name?: string;
	surname?: string;
	email?: string;
	userEmail?: string;
	fullName?: string;
	displayName?: string;
};

type MerchantDirectory = Record<
	string,
	{
		name: string;
		keys: MerchantActor[];
	}
>;

const API_KEY_DETAILS_STORAGE_KEY = 'api_key_details';

const LEGACY_UNSCOPED_CACHE_KEYS = ['cfm_case_types', 'cfm_check_types'];

// localStorage entries that belong to the session rather than to the browser
// profile. `sessionData` is the cross-tab session hand-off written by
// worker/shareSession.js and contains the token; the rest is per-session view
// state.
const SESSION_SCOPED_LOCAL_STORAGE_KEYS = [
	'sessionData',
	'selectedRowId',
	'prevPage',
];

const readStoredRecord = (key: string): Record<string, unknown> => {
	try {
		const rawValue = sessionStorage.getItem(key);
		if (!rawValue) {
			return {};
		}

		const parsedValue = JSON.parse(rawValue);
		return parsedValue &&
			typeof parsedValue === 'object' &&
			!Array.isArray(parsedValue)
			? (parsedValue as Record<string, unknown>)
			: {};
	} catch (error) {
		return {};
	}
};

const writeStoredRecord = (
	key: string,
	value: Record<string, unknown> | null
): void => {
	try {
		if (!value || !Object.keys(value).length) {
			sessionStorage.removeItem(key);
			return;
		}

		sessionStorage.setItem(key, JSON.stringify(value));
	} catch (error) {
		sessionStorage.removeItem(key);
	}
};

const createInitialState = () => ({
	token: sessionStorage.getItem('token') || '',
	token_expiry_date: sessionStorage.getItem('token_expiry_date') || '',
	is_impersonation: sessionStorage.getItem('is_impersonation') || '',
	user_claims: sessionStorage.getItem('user_claims'),
	user_id: sessionStorage.getItem('user_id'),
	user_name: sessionStorage.getItem('user_name'),
	user_surname: sessionStorage.getItem('user_surname'),
	user_email: sessionStorage.getItem('user_email'),
	user_created_at: sessionStorage.getItem('user_created_at'),
	merchant_name: sessionStorage.getItem('merchant_name'),
	merchant_id: sessionStorage.getItem('merchant_id'),
	merchant_created_at: sessionStorage.getItem('merchant_created_at'),
	billing_type: sessionStorage.getItem('billing_type') || '',
	payment_method: sessionStorage.getItem('payment_method') || '',
	module_availability: parseModuleAvailability(
		readStoredRecord('module_availability')
	),
	status: '',
	hasLoadedOnce: false,
	overallRisk: 0,
	channelRisk: 0,
	customerRisk: 0,
	productRisk: 0,
	geographicRisk: 0,
	channelHighRisk: false,
	channelVeryHighRisk: false,
	customerHighRisk: false,
	customerVeryHighRisk: false,
	productHighRisk: false,
	productVeryHighRisk: false,
	geographicHighRisk: false,
	geographicVeryHighRisk: false,
	channelRiskTriggerWeight: 0,
	customerRiskTriggerWeight: 0,
	geographicRiskTriggerWeight: 0,
	productRiskTriggerWeight: 0,
	customerRiskTooltip: '',
	channelRiskTooltip: '',
	geographicRiskTooltip: '',
	productRiskTooltip: '',
	whiteListUpdateList: [] as string[],
	version: 'Loading...',
	simpleRisk: {},
	simpleRiskTotal: 0,
	agentSimpleRiskTotal: 0,
	base64Org: '',
	loadOrg: false,
	whiteListHasUpdate: false,
	amlCreateDate: '',
	hasUpdate: false,
	hasAgentRiskUpdate: false,
	hasAssignAgentUpdate: false,
	noOfUnreadNotification: 0,
	notification: {},
	findCAUpdateCalledFromSidebar: false,
	showExpiryMessage: false,
	supportedCheckTypes: {},
	selectedSupportedCheckType: {},
	allMerchants: {} as MerchantDirectory,
	routeTo: null as unknown,
	apiKeyDetails: readStoredRecord(API_KEY_DETAILS_STORAGE_KEY),
	logDetails: {},
	eventLogDetails: {},
	passwordNeedsUpdate: false,
});

const clone = <T>(value: T): T => {
	if (typeof structuredClone === 'function') return structuredClone(value);
	return JSON.parse(JSON.stringify(value));
};

const clearPopupStateOnLogout = () => {
	try {
		popupService.closeAllPopups();
		popupService.resetPopupState();
		// Toasts live in a module-level singleton whose host sits above the
		// router-view, so one still on screen when the session ends would be read
		// as a failure of the login page it lands on. QGEN-5569.
		toastService.clear();
	} catch (error) {
		console.error('Failed to clear popup state during logout', error);
	}
};

const findActorInMerchant = (
	merchant: MerchantDirectory[string] | undefined,
	actorId: string
): MerchantActor | null => {
	if (!merchant || !Array.isArray(merchant.keys)) {
		return null;
	}

	return (
		merchant.keys.find(
			(actor) => String(actor?.id || '') === String(actorId || '')
		) || null
	);
};

const findActorInAllMerchants = (
	allMerchants: MerchantDirectory,
	actorId: string,
	currentMerchantId = ''
): MerchantActor | null => {
	const actorInCurrentMerchant = findActorInMerchant(
		allMerchants?.[currentMerchantId],
		actorId
	);
	if (actorInCurrentMerchant) {
		return actorInCurrentMerchant;
	}

	for (const [merchantId, merchant] of Object.entries(allMerchants || {})) {
		if (merchantId === currentMerchantId) {
			continue;
		}

		const actor = findActorInMerchant(merchant, actorId);
		if (actor) {
			return actor;
		}
	}

	return null;
};

export const useAuthStore = defineStore('auth', {
	state: () => clone(createInitialState()),
	getters: {
		authStatus: (state) => state.status,
		tokenExpiryDate: (state) => state.token_expiry_date,
		getVersion: (state) => state.version,
		getUserName: (state) =>
			Utils.formatName(state.user_name, state.user_surname),
		userID: (state) => state.user_id,
		merchantId: (state) => state.merchant_id,
		billingType: (state) => state.billing_type,
		paymentMethod: (state) => state.payment_method,
		moduleAvailability: (state): ModuleAvailability =>
			state.module_availability,
		isImpersonation: (state) => state.is_impersonation,
		getUnreadNotificationsNo: (state) => state.noOfUnreadNotification,
		getWhiteListHasUpdate: (state) => state.whiteListHasUpdate,
		getCAUpdateCalledFromSidebar: (state) =>
			state.findCAUpdateCalledFromSidebar,
		getMerchantNameById: (state) => (id: string) => {
			if (
				state.allMerchants &&
				Object.prototype.hasOwnProperty.call(state.allMerchants, id)
			) {
				return state.allMerchants[id].name;
			}
			return null;
		},
		getTimeUntilExpiry: (state) => {
			if (!state.token_expiry_date) return 0;
			const now = Date.parse(new Date() as unknown as string);
			const tokenExpiryDate = Date.parse(state.token_expiry_date);
			return tokenExpiryDate - now;
		},
		getUserEmailById: (state) => (actorId: string) => {
			const currentMerchantId = sessionStorage.getItem('merchant_id');
			const actor = findActorInAllMerchants(
				state.allMerchants,
				actorId,
				currentMerchantId || ''
			);
			if (!actor) {
				return null;
			}

			return actor.name;
		},
		getUserIdentityById: (state) => (actorId: string) => {
			const currentMerchantId = sessionStorage.getItem('merchant_id');
			const actor = findActorInAllMerchants(
				state.allMerchants,
				actorId,
				currentMerchantId || ''
			);
			if (!actor) {
				return null;
			}

			const displayName =
				actor.displayName ||
				actor.fullName ||
				Utils.formatName(actor.name || '', actor.surname || '') ||
				actor.name ||
				'';
			const email = actor.email || actor.userEmail || '';

			return {
				id: actor.id,
				displayName,
				email,
			};
		},
	},
	actions: {
		needs2FAVerification() {
			return sessionStorage.getItem('needs_2fa_verification') === 'true';
		},
		isAuthenticated() {
			if (sessionStorage.getItem('token_expiry_date') === '') {
				sessionStorage.setItem('authenticated_in_current_check', 'false');
				return false;
			}

			const now = Date.parse(new Date() as unknown as string);
			const tokenExpiryDate = Date.parse(
				sessionStorage.getItem('token_expiry_date') || ''
			);
			if (isNaN(tokenExpiryDate)) {
				sessionStorage.setItem('authenticated_in_current_check', 'false');
				return false;
			}
			// Token has actually expired (or is extremely close to it)
			if (now >= tokenExpiryDate) {
				sessionStorage.setItem('token_expiry_date', '');
				sessionStorage.setItem('authenticated_in_current_check', 'false');
				return false;
			}
			return true;
		},
		// user_claims is an array fresh from AUTH_SUCCESS but a comma-joined string
		// once rehydrated from sessionStorage, so `.includes()` on it directly is a
		// substring test rather than a membership test. Normalize first: this is
		// the bypass that grants Root everything, and it must not be one unlucky
		// claim name away from misfiring.
		isRoot() {
			return toClaimArray(this.user_claims).includes(SystemClaim.Root);
		},
		isAgent() {
			return toClaimArray(this.user_claims).includes(SystemClaim.Agent);
		},
		isRootOrOwner() {
			return this.isRoot();
		},
		// Module:level role gate. Root bypasses everything. Otherwise a module role
		// is satisfied by holding it at that level or higher, and anything else — a
		// system claim like "Agent"/"ServiceManagement" — by an exact match.
		can(requirement: string): boolean {
			if (this.isRoot()) {
				return true;
			}
			return satisfiesClaim(toClaimArray(this.user_claims), requirement);
		},
		isSessionExpired() {
			const currentAuth = sessionStorage.getItem(
				'authenticated_in_current_check'
			);
			const prevAuth = sessionStorage.getItem('authenticated_in_prev_check');
			return prevAuth === 'true' && currentAuth === 'false';
		},
		setRouterTo(to: unknown) {
			this.routeTo = to;
		},
		setPasswordNeedsUpdate(status: boolean) {
			this.passwordNeedsUpdate = status;
		},
		setNeeds2FAVerification(status: boolean) {
			sessionStorage.setItem('needs_2fa_verification', String(status));
		},
		setWhiteListHasUpdate(value: boolean) {
			this.whiteListHasUpdate = value;
		},
		setCAUpdateCalledFromSidebar(value: boolean) {
			this.findCAUpdateCalledFromSidebar = value;
		},
		setEventLogDetails(data: Record<string, unknown>) {
			this.eventLogDetails = data;
		},
		setShowExpiryMessage(value: boolean) {
			this.showExpiryMessage = value;
		},
		getShowExpiryMessage() {
			return this.showExpiryMessage;
		},
		getRouterTo() {
			return this.routeTo;
		},
		setUnreadNotificationsNo(value: number) {
			this.noOfUnreadNotification = value;
		},
		getNotification() {
			return this.notification;
		},
		setNotification(value: Record<string, unknown>) {
			this.notification = value;
		},
		setAMLCreateDate(value: string) {
			this.amlCreateDate = value;
		},
		getAMLCreateDate() {
			return this.amlCreateDate;
		},
		getApiKeyDetails() {
			if (this.apiKeyDetails && Object.keys(this.apiKeyDetails).length) {
				return this.apiKeyDetails;
			}

			return readStoredRecord(API_KEY_DETAILS_STORAGE_KEY);
		},
		setApiKeyDetails(value: Record<string, unknown> | null) {
			const normalizedValue =
				value && typeof value === 'object' && !Array.isArray(value)
					? value
					: {};
			this.apiKeyDetails = normalizedValue;
			writeStoredRecord(API_KEY_DETAILS_STORAGE_KEY, normalizedValue);
		},
		getLogDetails() {
			return this.logDetails;
		},
		setLogDetails(value: Record<string, unknown> | null) {
			this.logDetails = value || {};
		},
		setSelectedSupportedCheckType(value: Record<string, unknown> | null) {
			this.selectedSupportedCheckType = value || {};
		},
		getSelectedSupportedCheckType() {
			return this.selectedSupportedCheckType;
		},
		async setVersion() {
			const response = await makeCallToVersionAPI();
			this.version = response.version;
		},
		// GET /merchant/find/details is guarded with AllowRoles(platform:user)
		// (thanos-api merchant.go:31). This is awaited inside the login and 2FA
		// success paths, so a rejection here surfaces as a bogus "login failed" /
		// "invalid verification code" and the user never gets routed into the app.
		// Skip the call for users the gateway would reject, and never reject into
		// a caller's chain — the merchant name map is an enrichment, not a
		// prerequisite for being logged in.
		async loadMerchantDetails() {
			if (!this.can(ModuleRole.PlatformUser)) {
				return;
			}
			try {
				const resp = await getMerchantRepository().details();
				if (resp.data.success) {
					this.allMerchants = resp.data.data;
				}
			} catch (error) {
				console.error('Unable to load merchant details:', error);
			}
		},
		applyAuthSession(data: any) {
			setSessionStorage({ data });
			this[AUTH_SUCCESS](data);
		},
		async [AUTH_REQUEST](user: unknown) {
			clearSessionStorage();
			this.status = 'loading';
			try {
				const resp = await getPublicRepository().login(user);
				if (resp.data.success) {
					const tokenExpiryDate = new Date(resp.data.data.auth.expiry);
					tokenExpiryDate.setMinutes(tokenExpiryDate.getMinutes() - 1);
					resp.data.data.auth.expiry = tokenExpiryDate;
					this.showExpiryMessage = false;
					this.findCAUpdateCalledFromSidebar = false;
					setSessionStorage(resp.data);
					if (resp.data.data.auth.requires2FA) {
						this.setNeeds2FAVerification(true);
					} else {
						this.setNeeds2FAVerification(false);
						broadcastLoginAndSync();
					}
					this[AUTH_SUCCESS](resp.data.data);
					return resp.data.data;
				}
				return null;
			} catch (error) {
				this[AUTH_ERROR]();
				clearSessionStorage();
				throw error;
			}
		},
		async [AUTH_IMPERSONATION_REQUEST](payload: unknown) {
			this.status = 'loading';
			const resp = await getAuthRepository().impersonationLogin(payload);
			if (resp.data.success) {
				const tokenExpiryDate = new Date(resp.data.data.auth.expiry);
				tokenExpiryDate.setMinutes(tokenExpiryDate.getMinutes() - 8);
				resp.data.data.auth.token_expiry_date = tokenExpiryDate;
				setSessionStorage(resp.data);
				this[AUTH_SUCCESS](resp.data.data);
				return resp.data.data;
			}
			return null;
		},
		async [AUTH_RESET_IMPERSONATION_REQUEST]() {
			this.status = 'loading';
			const resp = await getAuthRepository().resetImpersonation();
			if (resp.data.success) {
				const tokenExpiryDate = new Date(resp.data.data.auth.expiry);
				tokenExpiryDate.setMinutes(tokenExpiryDate.getMinutes() - 5);
				resp.data.data.auth.token_expiry_date = tokenExpiryDate;
				setSessionStorage(resp.data);
				this[AUTH_SUCCESS](resp.data.data);
				return resp.data.data;
			}
			return null;
		},
		async refreshToken() {
			try {
				const resp = await getAuthRepository().refreshToken({
					JwtToken: this.token,
				});
				if (resp.data.success) {
					// Apply new logic similar to AUTH_SUCCESS
					this.token = resp.data.data.auth.token;
					const data = parseJwt(resp.data.data.auth.token);
					const tokenExpiryDate = new Date(resp.data.data.auth.expiry);
					tokenExpiryDate.setMinutes(tokenExpiryDate.getMinutes() - 1);
					resp.data.data.auth.expiry = tokenExpiryDate;

					this.token_expiry_date = resp.data.data.auth.expiry;
					sessionStorage.setItem('token', resp.data.data.auth.token);
					sessionStorage.setItem(
						'token_expiry_date',
						resp.data.data.auth.expiry
					);
					sessionStorage.setItem('user_claims', data.claims);

					const refreshData = {
						token: this.token,
						token_expiry_date: this.token_expiry_date.toString(),
						user_claims: data.claims,
					};
					localStorage.setItem(
						'syncTokenRefreshEvent',
						JSON.stringify(refreshData)
					);
					localStorage.removeItem('syncTokenRefreshEvent');

					return resp.data.data;
				}
				return null;
			} catch (error) {
				console.error('Failed to refresh token', error);
				throw error;
			}
		},
		syncTokenRefresh(data: any) {
			this.token = data.token;
			this.token_expiry_date = data.token_expiry_date;
			this.user_claims = data.user_claims;

			sessionStorage.setItem('token', data.token);
			sessionStorage.setItem('token_expiry_date', data.token_expiry_date);
			sessionStorage.setItem('user_claims', data.user_claims);
		},
		async [AUTH_LOGOUT]() {
			clearPopupStateOnLogout();
			this.token = '';
			this.token_expiry_date = '';
			this.is_impersonation = '';
			this.user_id = '';
			this.user_name = '';
			this.user_surname = '';
			this.user_email = '';
			this.user_claims = '';
			this.user_created_at = '';
			this.merchant_name = '';
			this.merchant_id = '';
			this.merchant_created_at = '';
			this.billing_type = '';
			this.payment_method = '';
			this.module_availability = {};
			clearSessionStorage();
			broadcastLogoutAndSync();
		},

		// Eager fetch on login. Populates the current merchant's billing type and
		// payment method so route guards and sidebar filters can gate on them
		// synchronously (see meta.hideForBillingType, meta.hideForPaymentMethod).
		// Silent-fail on 403/error: leaves values blank, which the filter treats
		// as "unknown" and lets the page through rather than mis-blocking a user
		// whose role can't read the endpoint.
		async fetchBillingType() {
			const merchantId = this.merchant_id;
			if (!merchantId) return;
			try {
				const resp: any =
					await getMerchantRepository().getBillingConfig(merchantId);
				const cfg = resp?.data?.data?.merchantBillingConfig || {};
				const type = cfg.billingType || '';
				const method = cfg.paymentMethod || '';
				this.billing_type = String(type);
				this.payment_method = String(method);
				sessionStorage.setItem('billing_type', String(type));
				sessionStorage.setItem('payment_method', String(method));
			} catch {
				this.billing_type = '';
				this.payment_method = '';
				sessionStorage.removeItem('billing_type');
				sessionStorage.removeItem('payment_method');
			}
		},
		// Eager fetch on login, same shape as fetchBillingType: which product
		// modules the plan includes, so the sidebar can lock a module the
		// organisation has not bought and the route guard can refuse it. Silent-fail
		// leaves the map empty, which every reader treats as "unknown" and opens
		// (common/moduleAvailability.ts), so a role that cannot reach the endpoint,
		// or an outage, never locks a paid-for feature.
		async fetchModuleAvailability() {
			if (!this.merchant_id) return;
			try {
				const resp: any = await getBillingRepository().getModuleAvailability();
				const modules = parseModuleAvailability(resp?.data?.data?.modules);
				this.module_availability = modules;
				writeStoredRecord('module_availability', modules);
			} catch {
				this.module_availability = {};
				writeStoredRecord('module_availability', null);
			}
		},
		[AUTH_SUCCESS](resp: any) {
			this.status = 'success';
			this.token = resp.auth.token;
			const data = parseJwt(resp.auth.token);
			this.token_expiry_date = resp.auth.expiry;
			this.is_impersonation = resp.auth.isImpersonation;
			this.user_claims = data.claims;
			this.user_id = resp.user.id;
			this.user_name = resp.user.name;
			this.user_surname = resp.user.surname;
			this.user_email = resp.user.email;
			this.user_created_at = resp.user.createdAt;
			this.merchant_name = resp.merchant.name;
			this.merchant_id = resp.merchant.id;
			this.merchant_created_at = resp.merchant.createdAt;
			this.hasLoadedOnce = true;
			// Fire-and-forget: hydrate billing_type so route guards and sidebar
			// filters can gate on it synchronously. Not while the OTP step is still
			// pending: the session then holds the interim 2FA token, which the
			// gateway accepts on the 2FA login route only, so both reads would fail
			// with 401. They run from applyAuthSession once the step completes.
			if (!this.needs2FAVerification()) {
				this.fetchBillingType();
				this.fetchModuleAvailability();
			}
		},
		[AUTH_ERROR]() {
			this.status = 'error';
			this.hasLoadedOnce = true;
		},
	},
});

// GET /userpreference/:userId is guarded with AllowRoles(platform:user)
// (thanos-api user_preference.go:28). This runs while the session is still being
// written, before the store holds the new claims, so the check reads them from
// the login response instead of going through authStore.can() — same semantics:
// Root passes, and platform:owner satisfies platform:user additively.
function canReadUserPreferences(claims: unknown): boolean {
	const rawClaims = toClaimArray(claims);
	return (
		rawClaims.includes(SystemClaim.Root) ||
		satisfiesModuleRole(rawClaims, ModuleRole.PlatformUser)
	);
}

function setSessionStorage(resp: any) {
	const data = parseJwt(resp.data.auth.token);

	// Login, impersonation login and reset-impersonation all land here and swap
	// the session without reloading the page, so the in-memory GET cache would
	// otherwise still hold the previous merchant's responses.
	clearRequestCache();

	sessionStorage.setItem('authenticated_in_prev_check', 'true');
	sessionStorage.setItem('authenticated_in_current_check', 'true');
	sessionStorage.setItem('token_expiry_date', resp.data.auth.expiry);
	sessionStorage.setItem('token', resp.data.auth.token);
	sessionStorage.setItem('is_impersonation', resp.data.auth.isImpersonation);

	sessionStorage.setItem('user_claims', data.claims);
	sessionStorage.setItem('user_id', resp.data.user.id);
	sessionStorage.setItem('user_name', resp.data.user.name);
	sessionStorage.setItem('user_surname', resp.data.user.surname);
	sessionStorage.setItem('user_email', resp.data.user.email);
	sessionStorage.setItem('user_created_at', resp.data.user.createdAt);
	sessionStorage.setItem('merchant_name', resp.data.merchant.name);
	sessionStorage.setItem('merchant_id', resp.data.merchant.id);
	sessionStorage.setItem('merchant_created_at', resp.data.merchant.createdAt);

	const needs2FASetup =
		resp.data.auth.claims &&
		resp.data.auth.claims.length === 1 &&
		resp.data.auth.claims[0] === SystemClaim.TwoFASetup;
	if (
		!resp.data.auth.requires2FA &&
		!needs2FASetup &&
		canReadUserPreferences(resp.data.auth.claims)
	) {
		getUserPreferenceRepository().getPreferencesAndSave(resp.data.user.id);
	}
}

function clearSessionStorage() {
	// Logout is a router push, not a page load, so the module-level GET cache
	// survives it and would be served to whoever logs in next in this tab.
	clearRequestCache();

	const version = sessionStorage.getItem('version');
	sessionStorage.setItem('version', version || '');

	// These CFM caches were once written to localStorage without a merchant
	// prefix, so they survived logout and were rendered for the next merchant to
	// use the browser profile. They are merchant-scoped in sessionStorage now;
	// drop any value a previous build left behind.
	LEGACY_UNSCOPED_CACHE_KEYS.forEach((key) => {
		localStorage.removeItem(key);
	});

	// Session data shared with other tabs holds the token itself, so it must not
	// outlive the session. Reference data (cached-constants, country,
	// nationalities, cfm_constants) and UI preferences are not session-scoped and
	// are deliberately left in place.
	SESSION_SCOPED_LOCAL_STORAGE_KEYS.forEach((key) => {
		localStorage.removeItem(key);
	});

	const keys = [
		'authenticated_in_prev_check',
		'authenticated_in_current_check',
		'token_expiry_date',
		'token',
		'is_impersonation',
		'user_claims',
		'module_role_claims',
		'user_id',
		'user_name',
		'user_surname',
		'user_email',
		'user_created_at',
		'merchant_name',
		'merchant_id',
		'merchant_created_at',
		'billing_type',
		'payment_method',
		'module_availability',
	];

	keys.forEach((key) => {
		sessionStorage.removeItem(key);
	});
}
